import { useEffect, useState, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Save,
  X,
  ShoppingCart,
  CreditCard,
  Smartphone,
  Receipt,
  Percent,
  Package,
  AlertCircle,
  Clock,
  Building2,
  FileText,
  MapPin,
} from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import {
  get_restaurant_setting,
  save_restaurant_setting,
  server_post_data,
} from "../ServiceConnection/serviceconnection";

const MEAL_SLOTS = [
  { key: "breakfast", label: "Breakfast" },
  { key: "lunch", label: "Lunch" },
  { key: "dinner", label: "Dinner" },
];

export default function DisplaySetting() {
  const { toast } = useToast();

  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [isFormValid, setIsFormValid] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    orderType: "dine",
    paymentType: "Cash",
    customPaymentType: "Paytm",
    restaurant_tax: "0",
    serviceCharge: "0",
    containerCharge: "0",
    tableId: "0",
    // restaurant identity
    restaurant_fssai: "",
    restaurant_gstin: "",
    restaurant_full_adrress: "",
    restaurant_city: "",
    restaurant_state: "",
    restaurant_country: "",
    // meal slots
    breakfast_check: false,
    breakfast_starttime: "",
    breakfast_endtime: "",
    lunch_check: false,
    lunch_starttime: "",
    lunch_endtime: "",
    dinner_check: false,
    dinner_starttime: "",
    dinner_endtime: "",
    special_check: false,
    special_starttime: "",
    special_endtime: "",
  });

  const handleFieldChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const { [field]: removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.orderType || formData.orderType === "null")
      newErrors.orderType = "Order type is required";
    if (!formData.paymentType || formData.paymentType === "null")
      newErrors.paymentType = "Payment type is required";
    if (!formData.customPaymentType || formData.customPaymentType === "null")
      newErrors.customPaymentType = "Custom payment type is required";
    if (!formData.restaurant_tax || formData.restaurant_tax === "null")
      newErrors.restaurant_tax = "GST is required";
    if (!formData.serviceCharge || formData.serviceCharge === "null")
      newErrors.serviceCharge = "Service charge is required";
    if (formData.containerCharge === "" || formData.containerCharge === "null")
      newErrors.containerCharge = "Container charge is required";

    // FSSAI — 14-digit numeric
    if (
      formData.restaurant_fssai &&
      !/^\d{14}$/.test(formData.restaurant_fssai)
    )
      newErrors.restaurant_fssai = "FSSAI licence number must be 14 digits";

    // GSTIN — 15-char alphanumeric
    if (
      formData.restaurant_gstin &&
      !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
        formData.restaurant_gstin.toUpperCase(),
      )
    )
      newErrors.restaurant_gstin = "Enter a valid 15-character GSTIN";

    // Meal slot validation — if toggled on, both times required
    MEAL_SLOTS.forEach(({ key, label }) => {
      if (formData[`${key}_check`]) {
        if (!formData[`${key}_starttime`])
          newErrors[`${key}_starttime`] = `${label} start time is required`;
        if (!formData[`${key}_endtime`])
          newErrors[`${key}_endtime`] = `${label} end time is required`;
      }
    });

    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    setIsFormValid(isValid);
    return isValid;
  }, [formData]);

  const fetchSettingsData = useCallback(async () => {
    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("flag", 1);
    fd.append("want_table_data", 1);

    try {
      const response = await server_post_data(get_restaurant_setting, fd);

      if (!response.data.error) {
        const r = response.data.message.data_restaurant[0];

        setFormData({
          orderType: r.default_order_type || "dine",
          paymentType: r.default_payment_type || "Cash",
          customPaymentType: r.default_custom_payment_type || "Paytm",
          restaurant_tax: r.restaurant_tax || "0",
          serviceCharge: r.serviceCharge || "0",
          containerCharge: r.containerCharge || "0",
          tableId: r.default_table_id || "0",
          // restaurant identity
          restaurant_fssai: r.restaurant_fssai || "",
          restaurant_gstin: r.restaurant_gstin || "",
          restaurant_full_adrress: r.restaurant_full_adrress || "",
          restaurant_city: r.restaurant_city || "",
          restaurant_state: r.restaurant_state || "",
          restaurant_country: r.restaurant_country || "",
          // meal slots
          breakfast_check: Boolean(r.breakfast_check),
          breakfast_starttime: r.breakfast_starttime || "",
          breakfast_endtime: r.breakfast_endtime || "",
          lunch_check: Boolean(r.lunch_check),
          lunch_starttime: r.lunch_starttime || "",
          lunch_endtime: r.lunch_endtime || "",
          dinner_check: Boolean(r.dinner_check),
          dinner_starttime: r.dinner_starttime || "",
          dinner_endtime: r.dinner_endtime || "",
          special_check: Boolean(r.special_check),
          special_starttime: r.special_starttime || "",
          special_endtime: r.special_endtime || "",
        });

        setTableData(response.data.message.data_table || []);
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast({
        title: "Error",
        description: "Failed to load settings data",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [toast]);

  const handleSave = useCallback(async () => {
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fill all required fields",
        variant: "destructive",
      });
      return;
    }

    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("default_order_type", formData.orderType);
    fd.append("restaurant_tax", formData.restaurant_tax);
    fd.append("default_payment_type", formData.paymentType);
    fd.append("default_custom_payment_type", formData.customPaymentType);
    fd.append("default_table_id", formData.tableId);
    fd.append("serviceCharge", formData.serviceCharge);
    fd.append("containerCharge", formData.containerCharge);
    // restaurant identity
    fd.append("restaurant_fssai", formData.restaurant_fssai);
    fd.append("restaurant_gstin", formData.restaurant_gstin.toUpperCase());
    fd.append("restaurant_full_adrress", formData.restaurant_full_adrress);
    fd.append("restaurant_city", formData.restaurant_city);
    fd.append("restaurant_state", formData.restaurant_state);
    fd.append("restaurant_country", formData.restaurant_country);
    fd.append("from_where", 1);

    // meal slots
    MEAL_SLOTS.forEach(({ key }) => {
      fd.append(`${key}_check`, formData[`${key}_check`] ? 1 : 0);
      fd.append(`${key}_starttime`, formData[`${key}_starttime`] || "");
      fd.append(`${key}_endtime`, formData[`${key}_endtime`] || "");
    });

    try {
      const response = await server_post_data(save_restaurant_setting, fd);
      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: "Display settings saved successfully",
        });
        fetchSettingsData();
      }
    } catch (error) {
      console.error("Save error:", error);
      toast({
        title: "Error",
        description: "Failed to save settings",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [formData, validateForm, toast, fetchSettingsData]);

  const handleCancel = useCallback(() => {
    fetchSettingsData();
  }, [fetchSettingsData]);

  useEffect(() => {
    fetchSettingsData();
  }, [fetchSettingsData]);
  useEffect(() => {
    validateForm();
  }, [formData, validateForm]);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground">
          Display Settings
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Configure default values for orders, payments, and charges
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Default Order Type */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ShoppingCart className="w-4 h-4 text-primary" />
              Default Order Type
            </CardTitle>
            <CardDescription>Select the default order type</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Select
                value={formData.orderType}
                onValueChange={(v) => handleFieldChange("orderType", v)}
              >
                <SelectTrigger
                  className={errors.orderType ? "border-destructive" : ""}
                >
                  <SelectValue placeholder="Select order type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dine">Dine in</SelectItem>
                  <SelectItem value="take">Takeaway</SelectItem>
                  <SelectItem value="delivery">Delivery</SelectItem>
                </SelectContent>
              </Select>
              {errors.orderType && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.orderType}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Default Payment Type */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-primary" />
              Default Payment Type
            </CardTitle>
            <CardDescription>Select the default payment method</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Select
                value={formData.paymentType}
                onValueChange={(v) => handleFieldChange("paymentType", v)}
              >
                <SelectTrigger
                  className={errors.paymentType ? "border-destructive" : ""}
                >
                  <SelectValue placeholder="Select payment type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Card">Card</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
              {errors.paymentType && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.paymentType}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Custom Payment Type */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Smartphone className="w-4 h-4 text-primary" />
              Custom Payment Type
            </CardTitle>
            <CardDescription>Select digital payment method</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Select
                value={formData.customPaymentType}
                onValueChange={(v) => handleFieldChange("customPaymentType", v)}
              >
                <SelectTrigger
                  className={
                    errors.customPaymentType ? "border-destructive" : ""
                  }
                >
                  <SelectValue placeholder="Select custom payment" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Googlepay">Google Pay</SelectItem>
                  <SelectItem value="Phone Pe">PhonePe</SelectItem>
                  <SelectItem value="Paytm">Paytm</SelectItem>
                </SelectContent>
              </Select>
              {errors.customPaymentType && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.customPaymentType}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* GST */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Receipt className="w-4 h-4 text-primary" />
              GST
            </CardTitle>
            <CardDescription>Select GST percentage</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Select
                value={String(formData.restaurant_tax)}
                onValueChange={(v) => handleFieldChange("restaurant_tax", v)}
              >
                <SelectTrigger
                  className={errors.restaurant_tax ? "border-destructive" : ""}
                >
                  <SelectValue placeholder="Select GST" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0%</SelectItem>
                  <SelectItem value="5">5%</SelectItem>
                  <SelectItem value="12">12%</SelectItem>
                  <SelectItem value="18">18%</SelectItem>
                  <SelectItem value="28">28%</SelectItem>
                </SelectContent>
              </Select>
              {errors.restaurant_tax && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.restaurant_tax}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Service Charge */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Percent className="w-4 h-4 text-primary" />
              Service Charge
            </CardTitle>
            <CardDescription>Select service charge percentage</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Select
                value={String(formData.serviceCharge)}
                onValueChange={(v) => handleFieldChange("serviceCharge", v)}
              >
                <SelectTrigger
                  className={errors.serviceCharge ? "border-destructive" : ""}
                >
                  <SelectValue placeholder="Select service charge" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0%</SelectItem>
                  <SelectItem value="2">2%</SelectItem>
                  <SelectItem value="5">5%</SelectItem>
                  <SelectItem value="8">8%</SelectItem>
                </SelectContent>
              </Select>
              {errors.serviceCharge && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.serviceCharge}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Container Charge */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Package className="w-4 h-4 text-primary" />
              Container Charge
            </CardTitle>
            <CardDescription>Enter container charge amount</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Input
                type="text"
                placeholder="Enter amount (e.g., 0, 10, 20)"
                value={formData.containerCharge}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value))
                    handleFieldChange("containerCharge", value);
                }}
                className={errors.containerCharge ? "border-destructive" : ""}
              />
              {errors.containerCharge && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.containerCharge}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Restaurant Identity ── */}
      <div>
        <h3 className="text-base font-semibold text-foreground flex items-center gap-2 mb-4">
          <Building2 className="w-4 h-4 text-primary" />
          Restaurant Identity
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* FSSAI Licence */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-4 h-4 text-primary" />
                FSSAI Licence Number
              </CardTitle>
              <CardDescription>
                14-digit Food Safety licence number
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Input
                  type="text"
                  placeholder="e.g. 12345678901234"
                  maxLength={14}
                  value={formData.restaurant_fssai}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*$/.test(value))
                      handleFieldChange("restaurant_fssai", value);
                  }}
                  className={
                    errors.restaurant_fssai ? "border-destructive" : ""
                  }
                />
                {errors.restaurant_fssai && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.restaurant_fssai}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* GSTIN */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Receipt className="w-4 h-4 text-primary" />
                GSTIN
              </CardTitle>
              <CardDescription>
                15-character GST Identification Number
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Input
                  type="text"
                  placeholder="e.g. 22AAAAA0000A1Z5"
                  maxLength={15}
                  value={formData.restaurant_gstin}
                  onChange={(e) =>
                    handleFieldChange(
                      "restaurant_gstin",
                      e.target.value.toUpperCase(),
                    )
                  }
                  className={
                    errors.restaurant_gstin ? "border-destructive" : ""
                  }
                />
                {errors.restaurant_gstin && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.restaurant_gstin}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Full Address — spans both columns */}
          <Card className="md:col-span-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <MapPin className="w-4 h-4 text-primary" />
                Restaurant Address
              </CardTitle>
              <CardDescription>
                Full address as it appears on bills and receipts
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Full street address */}
              <div className="space-y-2">
                <Label className="text-xs">Street / Full Address</Label>
                <Input
                  type="text"
                  placeholder="e.g. 12, MG Road, Near City Mall"
                  value={formData.restaurant_full_adrress}
                  onChange={(e) =>
                    handleFieldChange("restaurant_full_adrress", e.target.value)
                  }
                  className={
                    errors.restaurant_full_adrress ? "border-destructive" : ""
                  }
                />
                {errors.restaurant_full_adrress && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.restaurant_full_adrress}
                  </p>
                )}
              </div>

              {/* City · State · Country in a 3-col grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs">City</Label>
                  <Input
                    type="text"
                    placeholder="e.g. Bhopal"
                    value={formData.restaurant_city}
                    onChange={(e) =>
                      handleFieldChange("restaurant_city", e.target.value)
                    }
                    className={
                      errors.restaurant_city ? "border-destructive" : ""
                    }
                  />
                  {errors.restaurant_city && (
                    <p className="text-xs text-destructive flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.restaurant_city}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-xs">State</Label>
                  <Input
                    type="text"
                    placeholder="e.g. Madhya Pradesh"
                    value={formData.restaurant_state}
                    onChange={(e) =>
                      handleFieldChange("restaurant_state", e.target.value)
                    }
                    className={
                      errors.restaurant_state ? "border-destructive" : ""
                    }
                  />
                  {errors.restaurant_state && (
                    <p className="text-xs text-destructive flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.restaurant_state}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-xs">Country</Label>
                  <Input
                    type="text"
                    placeholder="e.g. India"
                    value={formData.restaurant_country}
                    onChange={(e) =>
                      handleFieldChange("restaurant_country", e.target.value)
                    }
                    className={
                      errors.restaurant_country ? "border-destructive" : ""
                    }
                  />
                  {errors.restaurant_country && (
                    <p className="text-xs text-destructive flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.restaurant_country}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ── Meal Time Slots ── */}
      <div>
        <h3 className="text-base font-semibold text-foreground flex items-center gap-2 mb-4">
          <Clock className="w-4 h-4 text-primary" />
          Meal Time Slots
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {MEAL_SLOTS.map(({ key, label }) => {
            const isOn = formData[`${key}_check`];
            return (
              <Card key={key} className={!isOn ? "opacity-60" : ""}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>{label}</span>
                    {/* Toggle */}
                    <button
                      type="button"
                      onClick={() => handleFieldChange(`${key}_check`, !isOn)}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${
                        isOn ? "bg-primary" : "bg-muted-foreground/30"
                      }`}
                    >
                      <span
                        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                          isOn ? "translate-x-4" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </CardTitle>
                  <CardDescription>
                    {isOn ? "Enabled — set start & end time" : "Disabled"}
                  </CardDescription>
                </CardHeader>
                {isOn && (
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Start Time</Label>
                        <Input
                          type="time"
                          value={formData[`${key}_starttime`]}
                          onChange={(e) =>
                            handleFieldChange(
                              `${key}_starttime`,
                              e.target.value,
                            )
                          }
                          className={
                            errors[`${key}_starttime`]
                              ? "border-destructive"
                              : ""
                          }
                        />
                        {errors[`${key}_starttime`] && (
                          <p className="text-xs text-destructive flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {errors[`${key}_starttime`]}
                          </p>
                        )}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">End Time</Label>
                        <Input
                          type="time"
                          value={formData[`${key}_endtime`]}
                          onChange={(e) =>
                            handleFieldChange(`${key}_endtime`, e.target.value)
                          }
                          className={
                            errors[`${key}_endtime`] ? "border-destructive" : ""
                          }
                        />
                        {errors[`${key}_endtime`] && (
                          <p className="text-xs text-destructive flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {errors[`${key}_endtime`]}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-4 pt-4 border-t">
        <Button
          variant="outline"
          onClick={handleCancel}
          disabled={showLoaderAdmin}
          className="gap-2"
        >
          <X className="w-4 h-4" />
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={showLoaderAdmin || !isFormValid}
          className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
        >
          {showLoaderAdmin ? (
            "Saving..."
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Settings
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
